/** @file Scale-drag math: per-axis or uniform factors from pointer distances along the gizmo axes. */
import { vec2 } from '@pose2d/math/vec2';
import type { Vec2 } from '@pose2d/math/types';
import { unitAt } from './_internal/unitAt';

/** Scale gizmo parts. */
export type ScalePart = 'uniform' | 'scaleX' | 'scaleY';

/** Drag record captured on press: grabbed part, gizmo X-axis world angle, origin and pointer (world). */
export interface ScaleDrag {
  part: ScalePart;
  rotation: number;
  origin: Vec2;
  start: Vec2;
}

/** n / d, or 1 when the press was (almost) on the pivot. */
function ratio(n: number, d: number): number {
  return Math.abs(d) < 1e-9 ? 1 : n / d;
}

/** Writes [factorX, factorY] relative to the press; constrain (Shift) makes an axis drag uniform. */
export function dragScale(drag: ScaleDrag, pointer: Vec2, constrain: boolean, out: Vec2): Vec2 {
  const s = vec2.sub([0, 0], drag.start, drag.origin);
  const p = vec2.sub([0, 0], pointer, drag.origin);
  if (drag.part === 'uniform') {
    const f = ratio(vec2.len(p), vec2.len(s));
    return vec2.set(out, f, f);
  }
  const axis = unitAt(drag.rotation + (drag.part === 'scaleY' ? 90 : 0), [0, 0]);
  const f = ratio(vec2.dot(p, axis), vec2.dot(s, axis));
  if (constrain) return vec2.set(out, f, f);
  return drag.part === 'scaleX' ? vec2.set(out, f, 1) : vec2.set(out, 1, f);
}
